import { useEffect, useRef } from "react";

/**
 * FIX-021 (mobile resume): runs `onResume` as soon as the page comes back to the foreground, instead of waiting for the next poll tick. Android / mobile browsers freeze timers and drop
 * connections while the page is hidden or the screen is off, so a poll that failed in the background would otherwise keep showing its error until the next 15 s tick (or a browser refresh).
 *
 * Triggers (all read-only by construction - the callback is whatever read the caller passes):
 *  - `document.visibilitychange` when `visibilityState === "visible"`,
 *  - `window` `focus`,
 *  - `window` `pageshow` (back/forward cache restore),
 *  - `window` `online` (connectivity is back; a failure while offline is exactly what this recovers).
 * Events that arrive while the page is hidden are ignored.
 *
 * One foreground cycle fires several of these almost together (`visibilitychange` + `focus` + `pageshow`): the FIRST one refreshes immediately, every other one inside
 * `RESUME_COALESCE_MS`, or while that refresh is still in flight, is dropped - at most one effective refresh per cycle, never a storm. The regular polling and the server-sent-event
 * subscription of the caller are untouched; this hook opens no connection and creates no timer. Listeners are removed on unmount.
 */
export const RESUME_COALESCE_MS = 1000;

export function useForegroundResume(onResume: () => unknown): void {
  const onResumeRef = useRef(onResume);
  onResumeRef.current = onResume;

  useEffect(() => {
    let lastFiredAtMs = Number.NEGATIVE_INFINITY;
    let inFlight = false;

    function resume(): void {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (inFlight || now - lastFiredAtMs < RESUME_COALESCE_MS) return;
      lastFiredAtMs = now;
      inFlight = true;
      Promise.resolve()
        .then(() => onResumeRef.current())
        .catch(() => undefined)
        .finally(() => {
          inFlight = false;
        });
    }

    document.addEventListener("visibilitychange", resume);
    window.addEventListener("focus", resume);
    window.addEventListener("pageshow", resume);
    window.addEventListener("online", resume);
    return () => {
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("focus", resume);
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("online", resume);
    };
  }, []);
}
